import React, { Component } from "react";
import moment from "moment";
import locale from "@bonliva-crm/locale";
import cns from "classnames";
import { ReactDatePickerProps as OriginalProps } from "react-datepicker";
import BetterReactDatePicker from "./BetterReactDatePicker";

import { withStyles } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import Input, { InputProps } from "@material-ui/core/Input";
import InputAdornment from "@material-ui/core/InputAdornment";
import ClearIcon from "@material-ui/icons/Clear";

import { Classes, ThemeType, themeObject } from "@theme";
import { textToDate } from "./textToDate";

import "react-datepicker/dist/react-datepicker.css";
import { isEmpty } from "lodash";

const clearBtnClassName = "react-date-picker__clear";

export type ReactDatePickerProps = Omit<
  OriginalProps,
  "isClearable" | "selected" | "onChange" | "value"
> & {
  align?: "left" | "right";
  autoOpen?: boolean;
  clearable?: boolean;
  fullWidth?: boolean;
  datePickerRef?: (ref: any) => any;
  inputFieldProps?: Partial<InputProps>;
  onChange: (m: moment.Moment) => any;
  value?: moment.Moment;
  withMuiPopper?: boolean; // true by default
};

type Props = ReactDatePickerProps & {
  classes: Classes<typeof styles>;
};

type State = {
  // when typing a date manually we need to store this value somewhere
  transitionalInput: string | null;
};

class ReactDatePicker extends Component<Props, State> {
  static defaultProps: Partial<Props> = {
    disabledKeyboardNavigation: true,
    withMuiPopper: true
  };

  ref = React.createRef<any>();

  state: State = {
    transitionalInput: null
  };

  componentDidMount() {
    const { datePickerRef } = this.props;

    if (!this.ref || !this.ref.current) {
      throw new Error("cannot find datepicker ref");
    }

    const onInputClickOrig = this.ref.current.onInputClick;
    const handleChangeOrig = this.ref.current.handleChange;

    datePickerRef && datePickerRef(this.ref.current);

    this.ref.current.onInputClick = (e) => {
      if (
        e.target.parentElement.parentElement.parentElement.classList.contains(
          clearBtnClassName
        )
      ) {
        this.props.onChange && this.props.onChange(null);
        return;
      }

      onInputClickOrig(e);
    };

    this.ref.current.onInputKeyDown = (e) => {
      if (e.key === "Enter" || e.key === "Escape" || e.key === "Tab") {
        this.ref.current.setOpen(false);
      }
    };

    this.ref.current.handleChange = (...allArgs) => {
      const e = allArgs[0];

      // if typing in custom input
      if (e.target.classList.contains("react-date-picker__custom_input")) {
        this.setState({ transitionalInput: e.target.value });
        return;
      }

      handleChangeOrig(...allArgs);
    };

    if (this.props.autoOpen) {
      this.ref.current.setOpen(true, true);
    }
  }

  handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { onChange } = this.props;
    const { transitionalInput } = this.state;

    if (transitionalInput) {
      let d = textToDate(transitionalInput);
      d = d || moment(transitionalInput, this.dateFormat);
      d = d.isValid() ? d : moment(transitionalInput); // best guess
      d.isValid() && onChange && onChange(d);
    }

    this.setState({ transitionalInput: null });
  };

  get dateFormat(): string {
    const { dateFormat } = this.props;
    return dateFormat
      ? Array.isArray(dateFormat)
        ? dateFormat[0]
        : dateFormat
      : themeObject.dateFormat;
  }

  get unicodeDateFormat(): string {
    const { dateFormat: dateFormatMaybeList } = this.props;

    const dateFormat = Array.isArray(dateFormatMaybeList)
      ? dateFormatMaybeList[0]
      : dateFormatMaybeList;
    // Replace occurences of momentjs' custom date format tokens with unicode ones
    // see https://momentjs.com/docs/#/displaying/format/ and https://date-fns.org/v2.30.0/docs/format
    const dateFormatUnicode = dateFormat
      ?.replace(/Y/g, "y")
      .replace(/D/g, "d")
      .replace(/W/g, "I")
      .replace(/G/g, "R");
    return !isEmpty(dateFormatUnicode)
      ? dateFormatUnicode
      : themeObject.dateFormatUnicode;
  }

  render() {
    const {
      minDate,
      maxDate,
      align = "left",
      classes,
      clearable = true,
      dateFormat: dateFormatProp,
      datePickerRef,
      fullWidth,
      inputFieldProps = {},
      onBlur,
      onChange,
      value: valueProp,
      withMuiPopper,
      disabled,
      ...rest
    } = this.props;

    const { transitionalInput } = this.state;

    const value = valueProp ? valueProp : undefined;
    const popperClassName = cns(`react-datepicker-popper-${align}`, {
      "react-datepicker-popper__withTime": this.props.showTimeSelect
    });

    const endAdornment =
      clearable && !disabled ? (
        <InputAdornment className="react-date-picker__clear" position="end">
          <IconButton className={classes.clear} onClick={() => onChange(null)}>
            <ClearIcon fontSize="small" />
          </IconButton>
        </InputAdornment>
      ) : null;

    return (
      <BetterReactDatePicker
        ref={this.ref}
        locale={locale.dateFnsLocale}
        data-align={align}
        // ref={datePickerRef}
        customInputRef="inputRef"
        customInput={
          <Input
            inputProps={{
              autoComplete: "off", // otherwise native autocomplete covers the calendar
              className: "react-date-picker__custom_input",
              style: {
                textAlign: align
              },
              value:
                typeof transitionalInput === "string"
                  ? transitionalInput
                  : value
                  ? value?.format(this.dateFormat)
                  : "",
              "data-offsets-popper": { top: 10 }
            }}
            endAdornment={endAdornment}
            className={classes.input}
            fullWidth={fullWidth}
            {...inputFieldProps}
          />
        }
        dateFormat={this.unicodeDateFormat}
        onBlur={this.handleBlur}
        onChange={(val) => onChange(moment(val))}
        // onFocus={onFocus}
        placeholderText=""
        popperClassName={popperClassName}
        selected={value ? value.toDate() : undefined}
        disabled={disabled}
        popperPlacement={align === "left" ? "bottom-start" : "bottom-end"}
        showMonthDropdown
        showYearDropdown
        openToDate={value ? value.toDate() : undefined}
        withMuiPopper={withMuiPopper}
        minDate={
          minDate && moment(minDate).isValid() ? moment(minDate).toDate() : undefined
        }
        maxDate={
          maxDate && moment(maxDate).isValid() ? moment(maxDate).toDate() : undefined
        }
        {...rest}
      />
    );
  }
}

const styles = (theme: ThemeType) => ({
  input: {},

  "@global": {
    ".react-datepicker-wrapper": {
      display: "block"
    },

    ".react-datepicker": {
      display: "flex"
    },

    ".react-datepicker__input-container": {
      display: "block"
    },

    ".react-datepicker-popper": {
      zIndex: 100
      // minWidth: "20rem" // empirically obtained value
    },

    ".react-datepicker-popper__withTime": {
      // minWidth: "20rem" // empirically obtained value
    },

    ".react-datepicker-popper-right .react-datepicker__triangle": {
      left: "inherit",
      right: 50
    },

    ".react-datepicker__time-list": {
      paddingLeft: [0, "!important"],
      paddingRight: [0, "!important"],
      overflowX: "hidden"
    }
  },

  clear: {
    height: 36,
    padding: 0,
    width: 36,
    whiteSpace: "nowrap"
  }
});

export default withStyles(styles as any)(ReactDatePicker);
